import { RootState, store } from 'modules/Editor/store';
import { selectZoom } from 'modules/Editor/store/editorSettingsSelector';
import { selectWidgets } from 'modules/Editor/store/infographSelector';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { WidgetId } from 'types/idTypes';
import { Subset } from 'types/object.types';
import { WidgetType } from 'types/widget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData, GroupWidgetData } from 'widgets/Widget.types';

import { Boundary, DistributeOption } from './AlignmentMenu.types';
import { computeAlignmentBoundary, getWidgetBoundary } from './common/WidgetBoundary.helpers';

type WidgetBoundary = {
  widgetId: WidgetId;
  boundary: Boundary;
};
type DistributeSortFn = (widgetA: WidgetBoundary, widgetB: WidgetBoundary) => number;

// Functions to sort the widgets based on distribute direction
const DISTRIBUTE_SORT_FN: { [key in DistributeOption]: DistributeSortFn } = {
  [DistributeOption.Horizontal]: (widgetA, widgetB) => {
    const { left: leftA, top: topA } = widgetA.boundary;
    const { left: leftB, top: topB } = widgetB.boundary;

    if (leftA < leftB) return -1;
    if (leftA > leftB) return 1;

    // If same left position, check top position
    if (topA < topB) return -1;
    if (topA > topB) return 1;
    return 0;
  },
  [DistributeOption.Vertical]: (widgetA, widgetB) => {
    const { left: leftA, top: topA } = widgetA.boundary;
    const { left: leftB, top: topB } = widgetB.boundary;

    if (topA < topB) return -1;
    if (topA > topB) return 1;

    // If same top position, check left position
    if (leftA < leftB) return -1;
    if (leftA > leftB) return 1;

    return 0;
  },
};

/**
 * Get the widget boundaries to distribute within the alignment boundary
 * Doesn't include widgets that take up entire alignment boundary
 *
 * @param alignmentBoundary boundary to distribute within
 * @param direction horizontal or vertical
 * @returns {
 *  endPositionWidgetId: widget id of the widget at the end of the alignment boundary
 *  widgetBoundaries: widget boundaries to distribute
 * }
 */
const getWidgetBoundariesToDistribute = (
  alignmentBoundary: Boundary,
  direction: DistributeOption,
): {
  endPositionWidgetId: WidgetId | null;
  widgetBoundaries: WidgetBoundary[];
} => {
  const storeState = store.getState() as RootState;

  const activeWidgets = selectActiveWidgets(storeState);
  const widgets = selectWidgets(storeState);
  const zoomLevel = selectZoom(storeState);

  const isHorizontalDistribute = direction === DistributeOption.Horizontal;
  const isVerticalDistribute = direction === DistributeOption.Vertical;

  const boundaryWidth = alignmentBoundary.right - alignmentBoundary.left;
  const boundaryHeight = alignmentBoundary.bottom - alignmentBoundary.top;

  let endPositionWidgetId: WidgetId | null = null;
  let endPosition = 0;
  const widgetBoundaries: WidgetBoundary[] = activeWidgets.reduce((boundaries, activeWidget) => {
    const widgetId = activeWidget.groupId || activeWidget.responsiveGroupId || activeWidget.id;
    const widgetBoundary = getWidgetBoundary(widgetId, widgets, zoomLevel);
    const widgetWidth = widgetBoundary.right - widgetBoundary.left;
    const widgetHeight = widgetBoundary.bottom - widgetBoundary.top;

    // Exclude widgets that take up entire boundary
    if (isHorizontalDistribute && widgetWidth === boundaryWidth) {
      return boundaries;
    }
    if (isVerticalDistribute && widgetHeight === boundaryHeight) {
      return boundaries;
    }

    const endBound = isHorizontalDistribute ? widgetBoundary.right : widgetBoundary.bottom;
    if (endBound > endPosition) {
      endPosition = endBound;
      endPositionWidgetId = widgetId;
    }

    boundaries.push({
      widgetId,
      boundary: widgetBoundary,
    });

    return boundaries;
  }, [] as WidgetBoundary[]);

  return {
    endPositionWidgetId,
    widgetBoundaries,
  };
};

/**
 * Distribute the active widgets equally within the bounding box in the specified direction
 *
 * @param direction
 * @returns
 */
const distributeWidgets = (direction: DistributeOption) => {
  const storeState = store.getState() as RootState;
  const dispatch = store.dispatch;

  const activeWidgets = selectActiveWidgets(storeState);
  if (activeWidgets.length < 3) return;

  const widgets = selectWidgets(storeState);
  const boundary = computeAlignmentBoundary(activeWidgets);
  const boundaryWidth = boundary.right - boundary.left;
  const boundaryHeight = boundary.bottom - boundary.top;

  const isHorizontalDistribute = direction === DistributeOption.Horizontal;

  // Get the widget boundaries for each active widget
  // Filters out widgets that take up entire boundary
  const { endPositionWidgetId, widgetBoundaries } = getWidgetBoundariesToDistribute(boundary, direction);

  // Sort the widget boundaries by the specified direction
  widgetBoundaries.sort(DISTRIBUTE_SORT_FN[direction]);

  // Get the last widget in the boundary (the rightmost or bottommost widget)
  // Should be sorted last
  const endPositionWidgetIdx = widgetBoundaries.findIndex((wBound) => wBound.widgetId === endPositionWidgetId);
  const [endWidgetBoundary] = widgetBoundaries.splice(endPositionWidgetIdx, 1);
  widgetBoundaries.push(endWidgetBoundary);

  // Get total width/height of all widgets depending on direction of distribution
  const actualWidgetBoundarySize = widgetBoundaries.reduce((acc, widgetBoundary) => {
    const { boundary: wBoundary } = widgetBoundary;
    const { top, left, bottom, right } = wBoundary;
    const width = right - left;
    const height = bottom - top;

    return isHorizontalDistribute ? acc + width : acc + height;
  }, 0);

  // Compute space between each widget
  // (Total boundary - total widget width/height) / (num widgets - 1)
  const spaceBetweenWidgets = isHorizontalDistribute
    ? (boundaryWidth - actualWidgetBoundarySize) / (widgetBoundaries.length - 1)
    : (boundaryHeight - actualWidgetBoundarySize) / (widgetBoundaries.length - 1);

  // Update each widget position by the calculated space between widgets
  const widgetUpdates: { widgetId: WidgetId; widgetData: Subset<AllWidgetData> }[] = [];
  let prevWidgetBound = 0;
  widgetBoundaries.forEach(({ widgetId, boundary: widgetBoundary }, index) => {
    const updateKey = direction === DistributeOption.Horizontal ? 'leftPx' : 'topPx';
    let newPos = prevWidgetBound + spaceBetweenWidgets;

    // First widget should be aligned to the left/top of the boundary
    if (index === 0) {
      newPos = isHorizontalDistribute ? boundary.left : boundary.top;
    }

    // Update prevWidgetBound for next widget calculation
    prevWidgetBound =
      newPos +
      (isHorizontalDistribute
        ? widgetBoundary.right - widgetBoundary.left
        : widgetBoundary.bottom - widgetBoundary.top);

    const delta = newPos - (isHorizontalDistribute ? widgetBoundary.left : widgetBoundary.top);

    // For groups need to update member widgets in the group
    if (getWidgetTypeFromId(widgetId) === WidgetType.Group) {
      const memberWidgetIds = (widgets[widgetId] as GroupWidgetData).memberWidgetIds;

      for (const memberWidgetId of memberWidgetIds) {
        const memberWidget = widgets[memberWidgetId];
        widgetUpdates.push({
          widgetId: memberWidgetId,
          widgetData: {
            [updateKey]: memberWidget[updateKey] + delta,
          },
        });
      }
      return;
    }

    const widget = widgets[widgetId];
    widgetUpdates.push({
      widgetId,
      widgetData: {
        [updateKey]: widget[updateKey] + delta,
      },
    });
  });

  dispatch(updateWidget(widgetUpdates));
};

/**
 * Returns true if the distribute option should be disabled
 * Distribute option is disabled if the widgets are already aligned on the given axis
 * (i.e.)
 *  Horizontal disabled if - left/mid/right aligned
 *  Vertical disabled if - top/mid/bottom aligned
 *
 * @param option
 * @returns
 */
const checkIsDistributeOptionDisabled = (option: DistributeOption) => {
  const storeState = store.getState() as RootState;
  const activeWidgets = selectActiveWidgets(storeState);

  const isHorizontalDistribute = option === DistributeOption.Horizontal;

  const boundary = computeAlignmentBoundary(activeWidgets);
  const { widgetBoundaries } = getWidgetBoundariesToDistribute(boundary, option);

  let isMinBoundAligned = true;
  let isMaxBoundAligned = true;
  let isMidAligned = true;

  const minBound = isHorizontalDistribute ? boundary.left : boundary.top;
  const maxBound = isHorizontalDistribute ? boundary.right : boundary.bottom;

  const boundarySize = maxBound - minBound;
  const midBound = minBound + boundarySize / 2;

  widgetBoundaries.forEach(({ boundary: widgetBoundary }) => {
    if (!isMinBoundAligned && !isMaxBoundAligned && !isMidAligned) return;

    const widgetMinBound = isHorizontalDistribute ? widgetBoundary.left : widgetBoundary.top;
    const widgetMaxBound = isHorizontalDistribute ? widgetBoundary.right : widgetBoundary.bottom;
    const widgetMidBound = widgetMinBound + (widgetMaxBound - widgetMinBound) / 2;

    if (Math.round(widgetMinBound) !== Math.round(minBound)) {
      isMinBoundAligned = false;
    }
    if (Math.round(widgetMaxBound) !== Math.round(maxBound)) {
      isMaxBoundAligned = false;
    }
    if (Math.round(widgetMidBound) !== Math.round(midBound)) {
      isMidAligned = false;
    }
  });

  return isMinBoundAligned || isMaxBoundAligned || isMidAligned;
};

export { distributeWidgets, checkIsDistributeOptionDisabled, DISTRIBUTE_SORT_FN };
